import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import { Button, Input } from "reactstrap";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import Header from "../../layout/Header/Header";
import { AuthContext } from "../../contexts/AuthContext";
import { posGet, posPost, posPatch, avisaError, mensajeError, numero, variante } from "./posApi";
import { useTienda, TiendaSelector } from "./TiendaSelector";
import SinPermiso from "./SinPermiso";
import FotoModelo from "../Articulos/FotoModelo";

const NIVEL = { Agotado: { color: "#dc2626", bg: "#fef2f2", icon: "fas fa-times-circle" }, Bajo: { color: "#d97706", bg: "#fffbeb", icon: "fas fa-exclamation-triangle" } };

function PosStockBajo() {
  const { user } = useContext(AuthContext);
  const ctx = useTienda(true);
  const { tienda, nombreTienda, master, setTienda } = ctx;
  const todas = tienda === "todas";
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [texto, setTexto] = useState("");
  const [nivel, setNivel] = useState("");
  const [pidiendo, setPidiendo] = useState(false);
  const puedePedir = !!(user && (user.posRecibir || user.posTraspasos));

  const cargar = useCallback(() => {
    if (!tienda) return;
    setCargando(true);
    posGet("/stockBajo", { area: tienda }).then(setDatos).catch(avisaError).finally(() => setCargando(false));
  }, [tienda]);
  useEffect(() => { cargar(); }, [cargar]);

  const visibles = useMemo(() => {
    if (!datos) return [];
    const t = texto.trim().toLowerCase();
    return datos.filas.filter((f) => (!nivel || f.nivel === nivel)
      && (!t || `${f.codigo} ${f.nombre} ${f.linea || ""} ${f.color || ""} ${f.talla || ""} ${f.tienda}`.toLowerCase().includes(t)));
  }, [datos, texto, nivel]);
  const agotados = datos ? datos.filas.filter((f) => f.nivel === "Agotado").length : 0;
  const bajos = datos ? datos.filas.length - agotados : 0;
  const piezas = visibles.reduce((s, f) => s + f.sugerido, 0);
  const pedibles = visibles.filter((f) => f.donador && !f.pedido);

  // Un pedido por tienda que pide y tienda que manda (el servidor los separa por la que manda)
  const pedir = async (filas) => {
    if (!filas.length) return;
    const porDestino = {};
    filas.forEach((f) => { (porDestino[f.area] = porDestino[f.area] || []).push(f); });
    const aQuien = {};
    filas.forEach((f) => { const k = `${f.tienda} ← ${f.donador.tienda}`; aQuien[k] = (aQuien[k] || 0) + f.donador.puede; });
    const ok = await Swal.fire({
      icon: "question", title: filas.length === 1 ? "¿Pedir traspaso?" : `¿Pedir ${filas.length} tallas y colores?`,
      html: Object.entries(aQuien).map(([k, n]) => `${k}: <b>${numero(n)}</b> pzs`).join("<br/>") + "<br/><br/><small>La tienda que tiene lo verá en Traspasos → Pedidos.</small>",
      showCancelButton: true, confirmButtonText: "Sí, pedir", cancelButtonText: "No",
    });
    if (!ok.value) return;
    setPidiendo(true);
    try {
      const hechos = [];
      for (const [area, fs] of Object.entries(porDestino)) {
        const r = await posPost("/solicitudes", {
          area, renglones: fs.map((f) => ({ articulos: f.articulos, colores: f.colores, tallas: f.tallas, cantidad: f.donador.puede, origen: f.donador.area })),
        });
        hechos.push(...r);
      }
      Swal.fire({ icon: "success", title: hechos.length === 1 ? "Pedido enviado" : `${hechos.length} pedidos enviados`, html: hechos.map((h) => `${h.origen}: ${numero(h.piezas)} pzs`).join("<br/>"), timer: 2500, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se pidió", text: mensajeError(e) });
    } finally {
      setPidiendo(false);
      cargar();
    }
  };

  const ajustar = async () => {
    const c = datos.config;
    const campo = (id, label, v, ayuda) => `<div style="text-align:left;margin-bottom:10px"><label style="font-weight:600;font-size:.9rem">${label}</label>
      <input id="${id}" type="number" min="1" class="swal2-input" style="margin:4px 0;max-width:100%" value="${v}"><div style="font-size:.78rem;color:#64748b">${ayuda}</div></div>`;
    const r = await Swal.fire({
      title: "Cómo se calcula el stock bajo",
      html: campo("cfgVenta", "Días de venta que se analizan", c.stockDiasVenta, "La venta promedio por día sale de este periodo.")
        + campo("cfgAlerta", "Alertar cuando alcance para menos de (días)", c.stockDiasAlerta, "Con la venta promedio, ¿para cuántos días alcanza lo que hay?")
        + campo("cfgCobertura", "Surtir para (días)", c.stockDiasCobertura, "El sugerido completa lo necesario para estos días."),
      showCancelButton: true, confirmButtonText: "Guardar", cancelButtonText: "Cancelar", focusConfirm: false,
      preConfirm: () => ({
        stockDiasVenta: document.getElementById("cfgVenta").value,
        stockDiasAlerta: document.getElementById("cfgAlerta").value,
        stockDiasCobertura: document.getElementById("cfgCobertura").value,
      }),
    });
    if (!r.value) return;
    try { await posPatch("/stockBajo/config", r.value); cargar(); } catch (e) { avisaError(e); }
  };

  const excel = () => {
    const filas = visibles.map((f) => ({
      ...(todas ? { Tienda: f.tienda } : {}), Estado: f.nivel, Codigo: f.codigo, Modelo: f.nombre, Linea: f.linea, Color: f.color || "", Talla: f.talla || "",
      Hay: f.existencia, EnCamino: f.enCamino, [`Vendidas${datos.config.stockDiasVenta}d`]: f.vendidas, AlcanzaDias: f.diasAlcanza, Surtir: f.sugerido,
      Pedido: f.pedido ? `${f.pedido.tienda} (${f.pedido.cantidad})` : "", PuedeMandar: f.donador ? f.donador.tienda : "", PiezasQuePuedeMandar: f.donador ? f.donador.puede : "", EnOtrasTiendas: f.otras,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filas), "Stock bajo");
    XLSX.writeFile(wb, `stock_bajo_${(nombreTienda || "tiendas").replace(/\s+/g, "_")}.xlsx`);
  };

  if (!(user && user.rolTiendas && user.posInventario)) return <SinPermiso que="ver el inventario" />;

  const cfg = datos && datos.config;
  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-bell mr-2" style={{ color: "#dc2626" }} />Stock bajo</h3>
            <p className="page-subtitle">Tallas y colores que se están vendiendo y están por acabarse en la tienda</p>
          </div>
          <div className="page-actions">
            <Button color="secondary" size="sm" href="/PosInventario"><i className="fas fa-warehouse mr-1" />Inventario</Button>
            <Button color="secondary" size="sm" href="/PuntoVenta"><i className="fas fa-th-large mr-1" />Menú</Button>
          </div>
        </div>

        <div className="filter-bar">
          <TiendaSelector ctx={ctx} permiteTodas />
          <div style={{ flex: 1, minWidth: 200 }}><label>Buscar</label><Input placeholder="Código, modelo, línea, color o talla" value={texto} onChange={(e) => setTexto(e.target.value)} /></div>
          <div><label>Mostrar</label>
            <Input type="select" value={nivel} onChange={(e) => setNivel(e.target.value)}>
              <option value="">Agotados y bajos</option><option value="Agotado">Solo agotados</option><option value="Bajo">Solo bajos</option>
            </Input>
          </div>
          <div style={{ flexDirection: "row", gap: 8 }}>
            <Button color="primary" onClick={cargar} disabled={cargando}>{cargando ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-sync" />}</Button>{" "}
            <Button color="warning" onClick={excel} disabled={!visibles.length}><i className="fas fa-file-excel mr-1" />Excel</Button>{" "}
            {puedePedir && pedibles.length > 0 && (
              <Button color="success" onClick={() => pedir(pedibles)} disabled={pidiendo} title="Pide a cada tienda lo que puede mandar">
                <i className="fas fa-truck mr-1" />Pedir todo ({pedibles.length})
              </Button>
            )}
          </div>
        </div>

        {cfg && (
          <div className="mb-3 p-2 d-flex flex-wrap align-items-center" style={{ background: "#f8fafc", borderRadius: 8, fontSize: "0.85rem", gap: 8 }}>
            <i className="fas fa-info-circle" style={{ color: "#64748b" }} />
            <span>Con la venta de los últimos <strong>{cfg.stockDiasVenta} días</strong>, avisa cuando lo que hay (más lo que viene en camino) alcanza para menos de <strong>{cfg.stockDiasAlerta} días</strong> y sugiere surtir para <strong>{cfg.stockDiasCobertura} días</strong>. Lo que no se ha vendido no aparece.</span>
            {master && user.posTiendas && <Button size="sm" color="primary" outline className="ml-auto" onClick={ajustar}><i className="fas fa-sliders-h mr-1" />Ajustar</Button>}
          </div>
        )}

        <div className="stats-row">
          <div className="stat-chip"><i className={NIVEL.Agotado.icon} style={{ color: NIVEL.Agotado.color }} />Agotados <span>{numero(agotados)}</span></div>
          <div className="stat-chip"><i className={NIVEL.Bajo.icon} style={{ color: NIVEL.Bajo.color }} />Por acabarse <span>{numero(bajos)}</span></div>
          <div className="stat-chip"><i className="fas fa-box" style={{ color: "#2563eb" }} />Piezas a surtir <span>{numero(piezas)}</span></div>
        </div>

        {todas && datos && datos.porTienda.length > 0 && (
          <div className="form-section mb-3">
            <div className="form-section-header"><i className="fas fa-store" /><h4 className="form-section-title">Por tienda</h4></div>
            <div className="form-section-body table-responsive">
              <table className="table table-sm mb-0">
                <thead><tr><th>Tienda</th><th className="text-right">Agotados</th><th className="text-right">Por acabarse</th><th className="text-right">Piezas a surtir</th><th /></tr></thead>
                <tbody>
                  {datos.porTienda.map((t) => (
                    <tr key={t.area}>
                      <td>{t.tienda}</td>
                      <td className="text-right" style={{ color: t.agotados ? NIVEL.Agotado.color : undefined, fontWeight: 700 }}>{numero(t.agotados)}</td>
                      <td className="text-right" style={{ color: t.bajos ? NIVEL.Bajo.color : undefined, fontWeight: 700 }}>{numero(t.bajos)}</td>
                      <td className="text-right">{numero(t.piezasSugeridas)}</td>
                      <td className="text-right"><Button size="sm" color="primary" outline onClick={() => setTienda(t.area)}>Ver tienda</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-sm table-dark-header">
            <thead>
              <tr>
                {todas && <th>Tienda</th>}
                <th>Estado</th><th>Modelo</th><th>Color · Talla</th>
                <th className="text-right">Hay</th><th className="text-right">En camino</th>
                <th className="text-right" title={cfg ? `Vendidas en ${cfg.stockDiasVenta} días` : ""}>Vendidas</th>
                <th className="text-right">Alcanza para</th><th className="text-right">Surtir</th><th>¿De dónde?</th>
              </tr>
            </thead>
            <tbody>
              {!visibles.length && (
                <tr><td colSpan={10} className="text-center text-muted py-4">
                  {cargando || !datos ? "Calculando…" : datos.filas.length ? "Nada con ese filtro" : <><i className="fas fa-check-circle mr-1" style={{ color: "#059669" }} />Todo lo que se vende tiene existencia suficiente</>}
                </td></tr>
              )}
              {visibles.slice(0, 3000).map((f) => {
                const n = NIVEL[f.nivel];
                return (
                  <tr key={`${f.area}-${f.articulos}-${f.colores || ""}-${f.tallas || ""}`} style={{ background: n.bg }}>
                    {todas && <td>{f.tienda}</td>}
                    <td style={{ color: n.color, fontWeight: 700, whiteSpace: "nowrap" }}><i className={`${n.icon} mr-1`} />{f.nivel}</td>
                    <td><div style={{ display: "flex", alignItems: "center", gap: 8 }}><FotoModelo url={f.urlFoto} nombre={`${f.codigo} · ${f.nombre}`} size={36} /><div><strong>{f.codigo}</strong> {f.nombre}{f.linea && <div className="small text-muted">{f.linea}</div>}</div></div></td>
                    <td>{variante(f) || "—"}</td>
                    <td className="text-right" style={{ fontWeight: 700 }}>{numero(f.existencia)}</td>
                    <td className="text-right">{f.enCamino ? numero(f.enCamino) : "—"}</td>
                    <td className="text-right">{numero(f.vendidas)}</td>
                    <td className="text-right" style={{ whiteSpace: "nowrap" }}>{f.diasAlcanza > 0 ? `${f.diasAlcanza} días` : "—"}</td>
                    <td className="text-right" style={{ fontWeight: 800 }}>{numero(f.sugerido)}</td>
                    <td style={{ fontSize: "0.82rem" }}>
                      {f.pedido ? (
                        <span style={{ color: "#2563eb", fontWeight: 600 }}><i className="fas fa-paper-plane mr-1" />Pedido a {f.pedido.tienda} ({numero(f.pedido.cantidad)})</span>
                      ) : f.donador ? (
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: 6 }}>
                          <span><strong>{f.donador.tienda}</strong> puede mandar {numero(f.donador.puede)}</span>
                          {puedePedir && <Button size="sm" color="success" outline disabled={pidiendo} onClick={() => pedir([f])}>Pedir</Button>}
                        </div>
                      ) : <span className="text-muted">Ninguna tienda tiene de sobra</span>}
                      {f.otras > 0 && <div className="small text-muted">{numero(f.otras)} en otras tiendas</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {visibles.length > 3000 && <div className="text-muted">Mostrando 3,000 de {numero(visibles.length)}; usa el buscador o descarga el Excel.</div>}
        </div>
      </div>
    </>
  );
}

export default PosStockBajo;
